import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { TONE, HEALTH_DOT, type Tone } from "@recursive-staff/ui";
import { api, type AskResponse } from "../api";
import { Icon } from "../lib/Icon";
import { useScope } from "../app/scope";
import { ErrorNote, Loading, Page } from "./common";

export function Dashboard() {
  const { client } = useScope();
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<AskResponse | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", client],
    queryFn: () => api.dashboard(client ?? undefined),
  });

  const askSeq = useRef(0);
  async function ask(question: string) {
    if (!question.trim() || asking) return;
    const seq = ++askSeq.current; // only the latest request may apply its result
    setQ(question);
    setAsking(true);
    setAnswer(null);
    try {
      const res = await api.ask(question);
      if (seq === askSeq.current) setAnswer(res);
    } catch {
      if (seq === askSeq.current) setAnswer({ answer: "Couldn’t reach the assistant just now.", sources: [], source: "computed", fallback: true });
    } finally {
      if (seq === askSeq.current) setAsking(false);
    }
  }

  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Overview</h1>
          <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>{data.scopeLabel} · support hours 8:00–18:00 ET</div>
        </div>
        <button style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 7, padding: "7px 11px", cursor: "pointer" }}>
          <Icon name="refresh-cw" size={14} color="#006747" /> Refresh
        </button>
      </div>

      {/* Ask across all clients */}
      <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" }}>
        <form onSubmit={(e) => { e.preventDefault(); ask(q); }} style={{ display: "flex", alignItems: "center", gap: 9, border: "1px solid #e2e0d8", borderRadius: 7, padding: "9px 12px" }}>
          <Icon name="sparkles" size={15} color="#006747" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask across all clients — tickets, feeds, security events, Survey…" style={{ flex: 1, border: 0, outline: 0, fontSize: 13.5, background: "transparent", minWidth: 0 }} />
          <button type="submit" disabled={asking} style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 6, padding: "6px 11px", cursor: asking ? "default" : "pointer", opacity: asking ? 0.6 : 1 }}>{asking ? "Asking…" : "Ask"}</button>
        </form>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
          {["Which clients saw digest failures this week?", "What is driving urgent tickets right now?", "Who is waiting longest without a reply?"].map((chip) => (
            <button key={chip} onClick={() => ask(chip)} disabled={asking} style={{ fontSize: 12, color: "#1d241f", background: "#f6f5f1", border: "1px solid #e7e5df", borderRadius: 999, padding: "5px 11px", cursor: asking ? "default" : "pointer", opacity: asking ? 0.6 : 1 }}>{chip}</button>
          ))}
        </div>
        {(asking || answer) && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f0efe9", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 16 }}>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: "#1d241f" }}>{asking ? "Thinking across every client…" : answer?.answer}</div>
            {answer && answer.sources.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 150 }}>
                <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88" }}>Sources</span>
                {answer.sources.map((s) => (
                  <span key={s} style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, color: "#006747", whiteSpace: "nowrap" }}>{s}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 14 }}>
        {data.kpis.map((k) => {
          const tone = TONE[k.tone as Tone];
          return (
            <div key={k.label} style={{ background: "#fff", borderRadius: 3, boxShadow: `inset 0 0 0 1px ${k.tone === "neutral" ? "rgba(0,0,0,.07)" : tone.fg + "55"}`, padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "#8a8e88" }}>{k.label}</span>
                <Icon name={k.icon} size={16} color={k.tone === "neutral" ? "#8a8e88" : tone.fg} />
              </div>
              <div style={{ fontSize: 30, fontWeight: 600, letterSpacing: "-1px", lineHeight: 1, marginTop: 12, fontFamily: "'Space Mono',monospace" }}>{k.value}</div>
              <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 9 }}>{k.delta}</div>
            </div>
          );
        })}
      </div>

      {/* Tickets by client + Needs attention */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(460px,1fr))", gap: 16 }}>
        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "15px 18px 12px" }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Tickets by client</h2>
            <Link to="/clients" style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".5px", textTransform: "uppercase" }}>All clients ›</Link>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr .6fr .7fr .8fr .6fr .9fr", padding: "8px 18px", fontSize: 10.5, fontWeight: 600, letterSpacing: ".8px", textTransform: "uppercase", color: "#a8aca6", borderTop: "1px solid #f4f3ee" }}>
            <span>Client</span><span style={{ textAlign: "right" }}>Open</span><span style={{ textAlign: "right" }}>At risk</span><span style={{ textAlign: "right" }}>1st reply</span><span style={{ textAlign: "right" }}>CSAT</span><span style={{ textAlign: "right" }}>Status</span>
          </div>
          {data.clients.map((r) => (
            <Link key={r.id} to="/clients/$clientId" params={{ clientId: r.id }} style={{ display: "grid", gridTemplateColumns: "1.4fr .6fr .7fr .8fr .6fr .9fr", alignItems: "center", padding: "11px 18px", fontSize: 13, borderTop: "1px solid #f4f3ee", cursor: "pointer", color: "inherit", textDecoration: "none" }}>
              <span style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: HEALTH_DOT[r.health] }} />
                {r.name} <span style={{ fontSize: 11, color: "#8a8e88" }}>{r.env}</span>
              </span>
              <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace" }}>{r.open}</span>
              <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace", color: r.atRisk ? "#b3261e" : "#8a8e88" }}>{r.atRisk}</span>
              <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace" }}>{r.firstReply}</span>
              <span style={{ textAlign: "right", fontFamily: "'Space Mono',monospace" }}>{r.csat}</span>
              <span style={{ textAlign: "right" }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: "4px 9px", borderRadius: 999, whiteSpace: "nowrap", background: TONE[r.health as Tone]?.bg ?? "#f0efe9", color: TONE[r.health as Tone]?.fg ?? "#6b706a" }}>
                  {r.health === "ok" ? "Healthy" : r.health === "warn" ? "Watch" : "At risk"}
                </span>
              </span>
            </Link>
          ))}
        </div>

        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(217,119,87,.45)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "15px 18px 10px" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#d97757" }} />
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, flex: 1 }}>Needs attention</h2>
            <Link to="/incidents" style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".5px", textTransform: "uppercase" }}>{data.security.length} incidents ›</Link>
          </div>
          {data.security.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 11, padding: "11px 18px", borderTop: "1px solid #f4f3ee" }}>
              <span style={{ width: 3, borderRadius: 2, background: TONE[s.tone as Tone].fg, flex: "none" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: TONE[s.tone as Tone].bg, color: TONE[s.tone as Tone].fg }}>{s.tone === "bad" ? "Major" : "Minor"}</span>
                </div>
                <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 2 }}>{s.tenant}</div>
              </div>
            </div>
          ))}
          {data.security.length === 0 && <div style={{ padding: "16px 18px", fontSize: 13, color: "#8a8e88" }}>No open incidents.</div>}
        </div>
      </div>

      {/* Delivery pipeline + Security summary */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }}>
        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Delivery pipeline</h2>
            <Link to="/board" style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".5px", textTransform: "uppercase" }}>Board ›</Link>
          </div>
          <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 4 }}>Escalated tickets moving through Survey</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 14 }}>
            {data.pipeline.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No linked Survey issues in scope.</div>}
            {data.pipeline.map((p) => (
              <div key={p.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 120, fontSize: 12, color: "#4a504a" }}>{p.label}</span>
                <div style={{ flex: 1, height: 8, background: "#f0efe9", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(100, p.n * 25)}%`, height: "100%", background: "#006747" }} />
                </div>
                <span style={{ width: 18, textAlign: "right", fontFamily: "'Space Mono',monospace", fontSize: 12 }}>{p.n}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: "#006747", borderRadius: 3, padding: "16px 18px", color: "#fff", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="shield-check" size={15} color="#9fe3c2" />
            <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "#9fe3c2" }}>Security · all clients</span>
          </div>
          <div style={{ fontSize: 17, fontWeight: 600, marginTop: 10, lineHeight: 1.35, letterSpacing: "-.3px" }}>
            {data.security.length} open {data.security.length === 1 ? "incident" : "incidents"} across clients.
          </div>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column" }}>
            {data.security.map((s, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: "1px solid rgba(255,255,255,.18)", fontSize: 12.5 }}>
                <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</span>
                <span style={{ color: "#9fe3c2", flex: "none", fontFamily: "'Space Mono',monospace", fontSize: 11 }}>{s.tenant}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
}
